import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, Card } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import EditableCellTable from '../editable_cell_table/EditableCellTable';
import { getConfigValueDefaultingToTrue, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	companyConfigRestCrudThunks,
	companyConfigRestCrudThunksForSupplier,
} from '../../thunks/company_config_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import posthog from 'posthog-js';

const { Content } = Layout;

const SupplierInventorySettingsPage: FC<any> = ({
	currentUser,
	companySettings,
	updateCompanySettings,
	getCompanySettings,
	companySettingsFetching,
}): React.ReactElement => {
	useEffect(() => {
		getCompanySettings(currentUser);
	}, [currentUser, getCompanySettings]);

	const generalSettingsArray = useMemo(
		() => [
			{
				settings: 'Enable GL Code For Inventory',
				key: 'enableGLCodeForInventory',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.enableGLCodeForInventory',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);

	const workOrderSettingsArray = useMemo(
		() => [
			{
				settings: 'Allow Reserved Parts',
				key: 'allowReservedParts',
				indicator: nullSafeGetOrElse('inventoryConfig.allowReservedParts', companySettings, false),
			},
			{
				settings: 'Auto Consume Requested Parts',
				key: 'autoConsumeShippedItems',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoConsumeShippedItems',
					companySettings,
					false
				),
			},
			{
				settings: 'Default Vendor Mandatory For Creating New Part',
				key: 'defaultVendorRequiredOnCreatingNewPart',
				indicator: getConfigValueDefaultingToTrue(
					companySettings,
					'inventoryConfig',
					'defaultVendorRequiredOnCreatingNewPart'
				),
			},
			{
				settings: 'Auto Return Unconsumed Parts On Work Order Closing',
				key: 'autoReturnUnconsumedPartsOnWorkOrderClosing',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoReturnUnconsumedPartsOnWorkOrderClosing',
					companySettings,
					false
				),
			},
			{
				settings: 'Create Purchase Requests Immediately For Stocked Parts (Skip Backorders)',
				key: 'skipBackordersForStockedPart',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.skipBackordersForStockedPart',
					companySettings,
					false
				),
			},
			{
				settings: 'Fulfill Purchase Requests Upon Work Order Completion',
				key: 'markPRFulfilledOnWOCompletion',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.markPRFulfilledOnWOCompletion',
					companySettings,
					false
				),
			},
			{
				settings: 'Allow to edit parts and equipment cost in Work Order',
				key: 'allowWorkOrderPartsAndEquipmentCostEdit',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.allowWorkOrderPartsAndEquipmentCostEdit',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);

	const puchaseRequestSettings = useMemo(
		() => [
			{
				settings: 'Auto Approve Purchase Requests For Stocked Parts',
				key: 'autoApprovePurchaseRequestForStockedPart',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoApprovePurchaseRequestForStockedPart',
					companySettings,
					false
				),
			},
			{
				settings: 'Auto Approve Purchase Requests For Non Stocked Parts',
				key: 'autoApprovePurchaseRequestForNonStockedPart',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoApprovePurchaseRequestForNonStockedPart',
					companySettings,
					false
				),
			},
			{
				settings:
					'Purchase Requests Created From Work Order, Cannot Be Shipped To An External Location',
				key: 'restrictShipToChangeOfPurchaseRequestCreatedFromWorkOrder',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.restrictShipToChangeOfPurchaseRequestCreatedFromWorkOrder',
					companySettings,
					false
				),
			},
			{
				settings:
					'Enable Ordering and Fulfillment of Purchase Request if there is no Purchase Order Associated',
				key: 'allowPurchaseRequestToBeFulfilledWithoutPurchaseOrder',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.allowPurchaseRequestToBeFulfilledWithoutPurchaseOrder',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);

	const stagingEnabled = posthog.isFeatureEnabled('staging_stock_location_enabled');

	const purchaseOrderSttings = useMemo(
		() => [
			{
				settings: 'Disable Purchase Orders',
				key: 'disablePurchaseOrders',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.disablePurchaseOrders',
					companySettings,
					false
				),
			},
			{
				settings: 'Auto Create Purchase Order For Stocked Parts',
				key: 'autoSendPurchaseOrderOnPurchaseRequestApprovalForStockedPart',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoSendPurchaseOrderOnPurchaseRequestApprovalForStockedPart',
					companySettings,
					false
				),
			},
			{
				settings: 'Auto Create Purchase Order For Non Stocked Parts',
				key: 'autoSendPurchaseOrderOnPurchaseRequestApprovalForNonStockedPart',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoSendPurchaseOrderOnPurchaseRequestApprovalForNonStockedPart',
					companySettings,
					false
				),
			},
			{
				settings: 'Automatically Email Purchase Order To Vendor',
				key: 'autoSendPurchaseOrderAsEmail',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.autoSendPurchaseOrderAsEmail',
					companySettings,
					false
				),
			},
			{
				settings: 'Accept Serial Numbers While Receiving Equipment',
				key: 'acceptSerialNumberForEquipmentReceiveInPO',
				indicator:
					typeof nullSafeGetOrElse('inventoryConfig', companySettings, {})
						.acceptSerialNumberForEquipmentReceiveInPO === 'undefined'
						? true
						: companySettings.inventoryConfig.acceptSerialNumberForEquipmentReceiveInPO,
			},
			{
				settings: 'Require Cost For Purchase Order Line Items',
				key: 'isPoCostMandatory',
				indicator: getConfigValueDefaultingToTrue(
					companySettings,
					'inventoryConfig',
					'isPoCostMandatory'
				),
			},
			...(stagingEnabled
				? [
						{
							settings:
								'Allow “Forwarding” (Shipped via a stock location with little to no storage time) Equipments Sent To a Location',
							key: 'allowEquipmentForwarding',
							indicator: nullSafeGetOrElse(
								'inventoryConfig.allowEquipmentForwarding',
								companySettings,
								false
							),
						},
				  ]
				: []),
		],
		[companySettings, stagingEnabled]
	);

	const workOrderSettings = useMemo(
		() => [
			{
				settings: 'Auto Assign Technician To Service Call Based On Routing',
				key: 'autoAssignTechnicianToServiceCallBasedOnRouting',
				indicator: nullSafeGetOrElse(
					'config.autoAssignTechnicianToServiceCallBasedOnRouting',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);
	const customersSettings = useMemo(
		() => [
			{
				settings: 'Hide Service Call Time Stamps ',
				key: 'hideServiceCallTimeStamps',
				indicator: nullSafeGetOrElse(
					'config.supplierManagedBuyerConfig.hideServiceCallTimeStamps',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);

	const transferSetting = useMemo(
		() => [
			{
				settings: 'Transfer Notes Mandatory',
				key: 'transferNoteMandatory',
				indicator: nullSafeGetOrElse(
					'inventoryConfig.transferNoteMandatory',
					companySettings,
					false
				),
			},
		],
		[companySettings]
	);

	const updateSettings = useCallback(
		(newSetting) => {
			const updatedSettings = {
				...companySettings,
				inventoryConfig: {
					...nullSafeGetOrElse('inventoryConfig', companySettings, {}),
					[newSetting.key]: newSetting.indicator,
				},
			};
			updateCompanySettings(updatedSettings);
		},
		[companySettings, updateCompanySettings]
	);

	const updateGeneralSettings = useCallback(
		(newSetting) => {
			const updatedSettings = {
				...companySettings,
				config: {
					...nullSafeGetOrElse('config', companySettings, {}),
					[newSetting.key]: newSetting.indicator,
				},
			};
			updateCompanySettings(updatedSettings);
		},
		[companySettings, updateCompanySettings]
	);
	const updateCustomerSettings = useCallback(
		(newSetting) => {
			const updatedSettings = {
				...companySettings,
				config: {
					...nullSafeGetOrElse('config', companySettings, {}),
					supplierManagedBuyerConfig: {
						...nullSafeGetOrElse('config.supplierManagedBuyerConfig', companySettings, {}),
						[newSetting.key]: newSetting.indicator,
					},
				},
			};
			updateCompanySettings(updatedSettings);
		},
		[companySettings, updateCompanySettings]
	);

	const overallSettingsArray = useMemo(
		() => [
			{
				title: 'Inventory Configurations - General',
				data: generalSettingsArray,
			},
			{
				title: 'Inventory Configurations - Work Orders',
				data: workOrderSettingsArray,
			},
			{
				title: 'Inventory Configurations - Purchase Requests',
				data: puchaseRequestSettings,
			},
			{
				title: 'Inventory Configurations - Purchase Orders',
				data: purchaseOrderSttings,
			},
			{
				title: 'Inventory Configurations - Transfers',
				data: transferSetting,
			},
			{
				title: 'Work Order Configurations',
				data: workOrderSettings,
				handleSave: updateGeneralSettings,
			},
			{
				title: 'Managed Customer Configurations',
				data: customersSettings,
				handleSave: updateCustomerSettings,
			},
		],
		[
			generalSettingsArray,
			workOrderSettingsArray,
			puchaseRequestSettings,
			purchaseOrderSttings,
			transferSetting,
			workOrderSettings,
			updateGeneralSettings,
			customersSettings,
			updateCustomerSettings,
		]
	);

	const getColumns = useCallback(
		(title) => [
			{
				title: title || 'Configuration',
				dataIndex: 'settings',
				width: '640px',
				render: (text) => <span style={{ fontSize: 16 }}>{text}</span>,
			},
			{
				dataIndex: 'indicator',
				width: '80px',
				editable: true,
			},
			{
				dataIndex: 'numerical',
				render: () => '',
			},
		],
		[]
	);

	return companySettingsFetching ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="buyerAdminSettingsPage">
			{overallSettingsArray.map((configurationGroup, index) => (
				<Row className="rowSpacing" key={index}>
					<Col style={{ maxWidth: 800, margin: 'auto' }} span={24}>
						<Card title={configurationGroup.title}>
							<EditableCellTable
								handleSave={configurationGroup.handleSave || updateSettings}
								columns={getColumns(configurationGroup.title)}
								data={configurationGroup.data}
							/>
						</Card>
					</Col>
				</Row>
			))}
		</Content>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companySettings: state.company_config.detail,
	companySettingsFetching: state.company_config.fetching,
});

const mapDispatchToProps = (dispatch) => ({
	getCompanySettings: (contact) =>
		dispatch(companyConfigRestCrudThunks(ROLE_TYPES.SUPPLIER, contact)),
	updateCompanySettings: (settings) =>
		dispatch(companyConfigRestCrudThunksForSupplier.update(settings)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierInventorySettingsPage)
);
